/**
 * CompleteStudentInfoSheet — claim-time gap-fill for REQUIRED enrollment
 * facts (grade / teacher / classroom / homeroom / lunch period).
 *
 * Schools can import students without the facts their campus marks
 * REQUIRED; the parent supplies them here from the campus's configured
 * lists. One student at a time: save advances to the next student with
 * missing facts, the last save closes the sheet. The step is SKIPPABLE
 * ("I'll do this later"), but placing an order for a student stays
 * blocked server-side until their facts are complete.
 *
 * Lunch period pre-fills from the campus's grade→lunch table (the server
 * sends the whole lookup) and re-derives when the parent picks a grade.
 *
 * Built on the raw `Sheet` primitive (not `FormSheet`) because a
 * successful save must be able to advance to the next student instead of
 * auto-closing.
 */

import { Button } from '@ordo/ui/button'
import { FormField } from '@ordo/ui/form-field'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@ordo/ui/sheet'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { useEffect, useMemo, useState } from 'react'

import {
  type EnrollmentFactDimension,
  type StudentNeedingInfo,
  type UpdateStudentEnrollmentInput,
  useStudentEnrollmentFormQuery,
  useUpdateStudentEnrollmentMutation,
} from '@/api/family'

/** PATCH body key per fact dimension (matches the Enrollment columns). */
const FACT_BODY_KEY: Record<EnrollmentFactDimension, keyof Omit<UpdateStudentEnrollmentInput, 'studentUserId'>> = {
  GRADE: 'grade',
  TEACHER: 'teacher',
  ROOM: 'roomNumber',
  HOMEROOM: 'homeroom',
  LUNCH_PERIOD: 'lunchPeriod',
}

export interface CompleteStudentInfoSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Students still missing REQUIRED facts — captured when the sheet opens. */
  students: StudentNeedingInfo[]
  /** Called after the LAST student is saved (not on skip/close). */
  onDone?: () => void
}

export function CompleteStudentInfoSheet({ open, onOpenChange, students, onDone }: CompleteStudentInfoSheetProps) {
  // The queue is captured once per open so saves (which refresh /family/me
  // and shrink the caller's list) don't reshuffle the remaining steps.
  const [queue, setQueue] = useState<StudentNeedingInfo[]>([])
  const [index, setIndex] = useState(0)
  const [values, setValues] = useState<Partial<Record<EnrollmentFactDimension, string>>>({})
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setQueue([])
      setIndex(0)
      return
    }
    setQueue((prev) => (prev.length > 0 ? prev : students))
  }, [open, students])

  const current = queue[index] ?? null
  const formQuery = useStudentEnrollmentFormQuery(open && current ? current.userId : null)
  const updateMutation = useUpdateStudentEnrollmentMutation()

  const form = formQuery.data?.form ?? null
  const campusConfirmation = formQuery.data?.campusConfirmation ?? null
  const campusRequired = campusConfirmation?.required ?? false
  // Ask exactly the REQUIRED-and-missing (askable) facts.
  const askedFacts = useMemo(() => (form?.facts ?? []).filter((fact) => fact.missing), [form])
  const [campusChoice, setCampusChoice] = useState('')

  // (Re)seed the pickers from the server's prefill whenever the form for
  // the current student arrives — lunch period arrives pre-derived, and the
  // campus prefills with the current (auto-assigned) pin so confirming is
  // one tap; a lone live campus preselects itself.
  useEffect(() => {
    const next: Partial<Record<EnrollmentFactDimension, string>> = {}
    for (const fact of form?.facts ?? []) {
      if (fact.missing) {
        next[fact.fact] = fact.prefill ?? ''
      }
    }
    setValues(next)
    const confirmation = formQuery.data?.campusConfirmation
    setCampusChoice(
      confirmation?.currentLocationId ??
        (confirmation?.campuses.length === 1 ? (confirmation.campuses[0]?.id ?? '') : ''),
    )
    setError(null)
  }, [form, formQuery.data?.campusConfirmation, current?.userId])

  function handlePick(fact: EnrollmentFactDimension, value: string) {
    setError(null)
    setValues((prev) => {
      const next = { ...prev, [fact]: value }
      // Grade drives lunch: re-derive from the campus lookup on every grade
      // pick (the parent can still override the lunch select afterwards).
      if (fact === 'GRADE' && 'LUNCH_PERIOD' in prev) {
        const derived = form?.gradeLunchPeriods.find(
          (row) => row.gradeLevel.trim().toLowerCase() === value.trim().toLowerCase(),
        )?.lunchPeriod
        if (derived) {
          next.LUNCH_PERIOD = derived
        }
      }
      return next
    })
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!current) {
      return
    }
    if (campusRequired && !campusChoice) {
      setError('Pick a campus to continue.')
      return
    }
    const unpicked = askedFacts.find((fact) => !values[fact.fact])
    if (unpicked) {
      setError(`Pick a ${unpicked.label.toLowerCase()} to continue.`)
      return
    }
    const input: UpdateStudentEnrollmentInput = { studentUserId: current.userId }
    if (campusRequired && campusChoice) {
      input.confirmLocationId = campusChoice
    }
    for (const fact of askedFacts) {
      input[FACT_BODY_KEY[fact.fact]] = values[fact.fact]
    }
    try {
      await updateMutation.mutateAsync(input)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save. Try again.')
      return
    }
    // Confirm-only save (no facts were askable yet — e.g. the student had no
    // campus, so no fact form existed): stay on this student; the refetch now
    // scoped to the confirmed campus surfaces any REQUIRED facts to fill.
    if (campusRequired && askedFacts.length === 0) {
      toast.success(`Saved ${current.displayName}`, { description: 'Campus confirmed.' })
      return
    }
    toast.success(`Saved ${current.displayName}`, { description: 'School info is complete.' })
    advance()
  }

  function advance() {
    if (index + 1 < queue.length) {
      setIndex(index + 1)
      return
    }
    onOpenChange(false)
    onDone?.()
  }

  if (!current) {
    return null
  }

  const stepLabel = queue.length > 1 ? ` (${index + 1} of ${queue.length})` : ''
  const schoolName = form?.location?.name ?? null
  // Facts already filled by someone else since the queue was captured —
  // nothing to ask; let the parent move on explicitly.
  const nothingToAsk = Boolean(formQuery.data) && askedFacts.length === 0 && !campusRequired

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onOpenChange(false)
        }
      }}>
      <SheetContent side="right" size="md" className="flex flex-col gap-0 p-0">
        <SheetHeader className="px-6 pb-4 pt-6">
          <SheetTitle>
            Complete {current.displayName}&apos;s school info{stepLabel}
          </SheetTitle>
          <SheetDescription>
            {schoolName ? `${schoolName} requires` : 'Their school requires'} this before you can order meals for{' '}
            {current.displayName}.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={(event) => void handleSubmit(event)} className="flex min-h-0 flex-1 flex-col">
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-2">
            {formQuery.isPending ? (
              <>
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </>
            ) : formQuery.error ? (
              <p className="text-sm text-destructive">
                Couldn&apos;t load the school&apos;s lists. Close and try again.
              </p>
            ) : nothingToAsk ? (
              <p className="text-sm text-muted-foreground">
                {current.displayName}&apos;s info is already complete — nothing to fill in.
              </p>
            ) : (
              <>
                {campusRequired && campusConfirmation ? (
                  <FormField
                    label="School campus"
                    htmlFor="complete-info-campus"
                    helperText={
                      campusConfirmation.reason === 'unconfirmed_auto'
                        ? `Confirm which campus ${current.displayName} attends.`
                        : `Pick which campus ${current.displayName} attends.`
                    }>
                    <Select value={campusChoice || undefined} onValueChange={(value) => setCampusChoice(value)}>
                      <SelectTrigger id="complete-info-campus">
                        <SelectValue placeholder="Pick a campus" />
                      </SelectTrigger>
                      <SelectContent>
                        {campusConfirmation.campuses.map((campus) => (
                          <SelectItem key={campus.id} value={campus.id}>
                            {campus.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                ) : null}
                {askedFacts.map((fact) => (
                  <FormField key={fact.fact} label={fact.label} htmlFor={`complete-info-${fact.fact.toLowerCase()}`}>
                    <Select
                      value={values[fact.fact] || undefined}
                      onValueChange={(value) => handlePick(fact.fact, value)}>
                      <SelectTrigger id={`complete-info-${fact.fact.toLowerCase()}`}>
                        <SelectValue placeholder={`Pick a ${fact.label.toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {fact.options.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                ))}
              </>
            )}
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-4">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              I&apos;ll do this later
            </Button>
            {nothingToAsk ? (
              <Button type="button" onClick={advance}>
                {index + 1 < queue.length ? 'Next student' : 'Done'}
              </Button>
            ) : (
              <Button type="submit" disabled={formQuery.isPending || updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving…' : index + 1 < queue.length ? 'Save & next student' : 'Save'}
              </Button>
            )}
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
